// inventoryService.js - API client for Inventory feature
// Handles all inventory-related API calls with authentication

const API_BASE_URL = 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token (idToken for CIAM)
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Helper to build headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * List inventory entities with filtering and pagination
 * @param {object} filters - Filter options
 * @returns {Promise<object>} - { success, items, continuationToken, totalCount, hiddenCount, facets, limits }
 */
export async function getInventory(filters = {}) {
    try {
        const headers = await getHeaders();
        const params = new URLSearchParams();

        // Add all filters to query params
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });

        const response = await fetch(`${API_BASE_URL}/inventory/list?${params}`, {
            method: 'GET',
            headers,
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
            const error = new Error(data.error || `Failed to list inventory (${response.status})`);
            error.statusCode = response.status;
            error.upgradeMessage = data.upgradeMessage;
            error.upgradeUrl = data.upgradeUrl;
            error.inventoryEnabled = data.inventoryEnabled;
            error.tier = data.tier;
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error listing inventory:', error);
        throw error;
    }
}

/**
 * Get details for a specific entity
 * @param {string} entityId - The entity ID
 * @returns {Promise<object>} - { success, entity, history }
 */
export async function getEntity(entityId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/inventory/entities/${entityId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get entity (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting entity:', error);
        throw error;
    }
}

/**
 * Get parent and child relationships for an entity
 * @param {string} entityId - The entity ID
 * @returns {Promise<object>} - { success, entityId, parents, children, counts }
 */
export async function getEntityRelations(entityId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/inventory/entities/${entityId}/relations`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get relations (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting entity relations:', error);
        throw error;
    }
}

/**
 * Execute an action on an entity
 * @param {string} action - Action name (scanNow, portScan, validateFinding)
 * @param {string} entityId - The entity ID
 * @param {object} options - Optional action parameters
 * @returns {Promise<object>} - { success, action, entityId, jobId, message }
 */
export async function executeAction(action, entityId, options = {}) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/inventory/action`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ action, entityId, options }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to execute action (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error executing action:', error);
        throw error;
    }
}

/**
 * Export inventory data as CSV or JSON
 * @param {string} format - 'csv' or 'json'
 * @param {object} filters - Filter options
 * @returns {Promise<Blob>} - File blob for download
 */
export async function exportInventory(format = 'csv', filters = {}) {
    try {
        const headers = await getHeaders();
        // Remove Content-Type for blob response
        delete headers['Content-Type'];

        const params = new URLSearchParams({ format });
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value);
            }
        });

        const response = await fetch(`${API_BASE_URL}/inventory/export?${params}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            // Try to parse error as JSON
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Export failed (${response.status})`);
            }
            throw new Error(`Export failed (${response.status})`);
        }

        return await response.blob();
    } catch (error) {
        console.error('Error exporting inventory:', error);
        throw error;
    }
}

/**
 * Download exported inventory file
 * @param {string} format - 'csv' or 'json'
 * @param {object} filters - Filter options
 */
export async function downloadInventory(format = 'csv', filters = {}) {
    const blob = await exportInventory(format, filters);
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `driftalarm-inventory-${dateStr}.${format}`;

    // Create download link and trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

// Entity type display helpers - uses CSS variables for theme awareness
export const ENTITY_TYPE_CONFIG = {
    seed_asset: { label: 'Seed Assets', icon: '🌱', color: 'var(--accent-primary, #c5003c)' },
    domain: { label: 'Domains', icon: '🌐', color: 'var(--accent-tertiary, #00D9FF)' },
    subdomain: { label: 'Subdomains', icon: '📡', color: 'var(--accent-tertiary, #00D9FF)' },
    ip: { label: 'IP Addresses', icon: '🔢', color: 'var(--severity-low, #39FF14)' },
    port: { label: 'Ports', icon: '🔌', color: 'var(--severity-medium, #FFB800)' },
    service: { label: 'Services', icon: '⚙️', color: 'var(--neon-purple, #9D4EDD)' },
    url: { label: 'URLs', icon: '🔗', color: 'var(--accent-tertiary, #00D9FF)' },
    finding: { label: 'Findings', icon: '⚠️', color: 'var(--severity-critical, #ff0000)' }
};

export const SEVERITY_CONFIG = {
    critical: { label: 'Critical', color: 'var(--severity-critical, #ff0000)', bgColor: 'rgba(255, 0, 0, 0.15)' },
    high: { label: 'High', color: 'var(--severity-high, #ff6b35)', bgColor: 'rgba(255, 107, 53, 0.15)' },
    medium: { label: 'Medium', color: 'var(--severity-medium, #FFB800)', bgColor: 'rgba(255, 184, 0, 0.15)' },
    low: { label: 'Low', color: 'var(--severity-low, #39FF14)', bgColor: 'rgba(57, 255, 20, 0.15)' },
    info: { label: 'Info', color: 'var(--severity-info, #4a9eff)', bgColor: 'rgba(74, 158, 255, 0.15)' }
};

// Default export for convenience
const inventoryService = {
    getInventory,
    getEntity,
    getEntityRelations,
    executeAction,
    exportInventory,
    downloadInventory,
    ENTITY_TYPE_CONFIG,
    SEVERITY_CONFIG
};

export default inventoryService;
